// frontend/src/AthleteLineupsReadOnly.js
import { useEffect, useMemo, useRef, useState } from "react";
import { PracticeAPI, apiFetch } from "./api";
import { formatPracticeTimeRange } from "./utils";

export default function AthleteLineupsReadOnly({ date: initialDate, user }) {
  const today = useMemo(() => ymd(new Date()), []);
  const [date, setDate] = useState(() => initialDate || today);

  // Re-sync to the requested date whenever it changes (e.g. clicking a different
  // practice's lineup link from Home without this component unmounting)
  useEffect(() => {
    if (initialDate && initialDate !== date) {
      setDate(initialDate);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDate]);

  // Normalize team to uppercase to match backend storage (normalizeTeam uppercases)
  const userTeam = typeof user?.team === "string" ? user.team.trim().toUpperCase() : "";

  // Teams (config list) for the toggle — always shown regardless of publish state
  const [teams, setTeams] = useState([]); // [{id, name, ...}]

  // User's explicit team choice (null = "not yet chosen, use default")
  const [chosenTeam, setChosenTeam] = useState(null);

  const [practiceId, setPracticeId] = useState(null);
  const [dayPractices, setDayPractices] = useState([]);
  const [isPublished, setIsPublished] = useState(false);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [boats, setBoats] = useState([]);
  const [checkins, setCheckins] = useState({});
  const [publishedNote, setPublishedNote] = useState("");
  const [positionColors, setPositionColors] = useState({});
  const [attendanceMode, setAttendanceMode] = useState(false);

  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        const rows = await apiFetch('/teams');
        if (!abort) setTeams(Array.isArray(rows) ? rows : []);
      } catch (e) {
        if (!abort) setTeams([]);
      }
    })();
    return () => { abort = true; };
  }, []);

  // Effective team: use chosen value if valid, else the athlete's own team, else first configured team
  const teamNames = useMemo(() => teams.map(t => t.name), [teams]);
  const selectedTeam = useMemo(() => {
    if (!teamNames.length) return "";
    const findTeam = (want) => teamNames.find(t => t.trim().toUpperCase() === want);
    if (chosenTeam) {
      const match = findTeam(chosenTeam.trim().toUpperCase());
      if (match) return match;
    }
    if (userTeam) {
      const match = findTeam(userTeam);
      if (match) return match;
    }
    return teamNames[0];
  }, [teamNames, chosenTeam, userTeam]);

  // Reset chosen team when date changes
  const prevDate = useRef(date);
  if (prevDate.current !== date) {
    prevDate.current = date;
    if (chosenTeam !== null) setChosenTeam(null);
  }

  // Load practices for the selected team/date, then pick the published one
  useEffect(() => {
    let abort = false;
    setDayPractices([]);
    setPracticeId(null);
    setIsPublished(false);
    if (!selectedTeam) return;
    (async () => {
      try {
        const [items, statusResult] = await Promise.all([
          PracticeAPI.listRange(date, date, selectedTeam),
          apiFetch(`/lineups/${date}/status?team=${encodeURIComponent(selectedTeam)}`).catch(() => null),
        ]);
        if (abort) return;
        const normalized = (Array.isArray(items) ? items : []).map((p) => {
          const startTime = p.startTime || p.start_time || p.time || null;
          const endTime = p.endTime || p.end_time || null;
          return {
            id: p.id,
            date: String(p.date || "").slice(0, 10),
            startTime: startTime ? String(startTime).slice(0, 5) : null,
            endTime: endTime ? String(endTime).slice(0, 5) : null,
            type: p.type || p.practiceType || p.practice_type || null,
            team: p.team || null,
          };
        })
          .filter(isPracticeType)
          .filter(p => !p.team || p.team.trim().toUpperCase() === selectedTeam);
        normalized.sort((a, b) => (a.startTime || "").localeCompare(b.startTime || ""));
        setDayPractices(normalized);

        // Trust the status endpoint's practiceId directly: it is not squad-filtered,
        // while `normalized` (from /practices/range) is, so a practice scoped to
        // squads that exclude this athlete would otherwise be silently dropped here
        // even though a lineup was published for their team.
        const published = Boolean(statusResult?.published);
        const publishedPracticeId = Number(statusResult?.practiceId ?? 0) || null;
        setIsPublished(published);
        setPracticeId(publishedPracticeId ?? (normalized[0]?.id ?? null));
      } catch (e) {
        if (!abort) { setDayPractices([]); setPracticeId(null); setIsPublished(false); }
      }
    })();
    return () => { abort = true; };
  }, [date, selectedTeam]);

  // Load published lineup for the chosen practice + team
  useEffect(() => {
    let abort = false;
    if (!practiceId || !selectedTeam) {
      setBoats([]);
      setCheckins({});
      setPublishedNote("");
      return;
    }
    (async () => {
      try {
        setLoading(true);
        setErr("");
        const data = await apiFetch(
          `/lineups/${practiceId}/published?team=${encodeURIComponent(selectedTeam)}`
        );
        if (abort) return;
        setBoats(Array.isArray(data?.boats) ? data.boats : []);
        setCheckins(data?.checkins && typeof data.checkins === "object" ? data.checkins : {});
        setPublishedNote(typeof data?.note === "string" ? data.note : "");
      } catch (e) {
        if (e?.status === 403) {
          if (!abort) { setBoats([]); setCheckins({}); setPublishedNote(""); setErr(""); }
          return;
        }
        if (!abort) { setErr(e?.message || "Failed to load lineups"); setBoats([]); setCheckins({}); setPublishedNote(""); }
      } finally {
        if (!abort) setLoading(false);
      }
    })();
    return () => { abort = true; };
  }, [practiceId, selectedTeam]);

  // Load position colors
  useEffect(() => {
    let abort = false;
    apiFetch("/positions").then(positions => {
      if (abort) return;
      const colorMap = {};
      (Array.isArray(positions) ? positions : []).forEach(p => {
        if (p?.name && p?.color) colorMap[p.name.toLowerCase()] = p.color;
      });
      setPositionColors(colorMap);
    }).catch(() => { if (!abort) setPositionColors({}); });
    return () => { abort = true; };
  }, []);

  function prevDay() { setDate(d => ymd(addDays(d, -1))); }
  function nextDay() { setDate(d => ymd(addDays(d, 1))); }

  async function toggleCheckin(userId) {
    if (!attendanceMode || !practiceId || !userId) return;
    const current = checkins?.[userId] || null;
    const next = current === "here" ? "not_here" : "here";
    setCheckins(prev => ({ ...(prev || {}), [userId]: next }));
    try {
      await apiFetch(
        `/lineups/${practiceId}/checkins?team=${encodeURIComponent(selectedTeam)}`,
        { method: "POST", body: JSON.stringify({ userId, status: next }) }
      );
    } catch (e) {
      setCheckins(prev => ({ ...(prev || {}), [userId]: current }));
      setErr(e?.message || "Failed to save check-in");
    }
  }

  function fmtPracticeOption(p) {
    const timeLabel = formatPracticeTimeRange(p) || "All day";
    const typeRaw = String(p.type || "").trim().toLowerCase();
    const typeLabel = typeRaw.includes("regatta") ? "Regatta" : typeRaw.includes("event") ? "Event" : "Practice";
    return `${timeLabel} — ${typeLabel}`;
  }

  const teamsLoading = teams.length === 0 && !selectedTeam;

  return (
    <div className="stack">
      {/* Pill toolbar */}
      <div className="lineup-pill-bar">
        <div className="lineup-pill-filters">
          {/* Date prev/next */}
          <button className="lineup-pill-nav-btn" onClick={prevDay} title="Previous day">←</button>
          <input
            type="date"
            className="lineup-pill-select lineup-pill-select--date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{ width: 150 }}
          />
          <button className="lineup-pill-nav-btn" onClick={nextDay} title="Next day">→</button>
          <button className="lineup-pill-nav-btn" onClick={() => setDate(today)}>Today</button>

          {/* Team toggle: defaults to the athlete's own team, switchable to any configured team */}
          {teamNames.length > 0 && (
            <div className="btn-group" role="group" aria-label="Team">
              {teamNames.map((t) => (
                <button
                  key={t}
                  type="button"
                  className={`btn ghost small ${selectedTeam === t ? "active" : ""}`}
                  aria-pressed={selectedTeam === t}
                  onClick={() => setChosenTeam(t)}
                >
                  {t}
                </button>
              ))}
            </div>
          )}

          {/* Practice */}
          {dayPractices.length > 1 && (
            <select
              className="lineup-pill-select lineup-pill-select--wide"
              value={practiceId || ""}
              onChange={(e) => setPracticeId(e.target.value ? Number(e.target.value) : null)}
              title="Practice"
            >
              {dayPractices.map(p => (
                <option key={p.id} value={p.id}>{fmtPracticeOption(p)}</option>
              ))}
            </select>
          )}
        </div>

        {/* Attendance toggle */}
        <div className="lineup-pill-actions">
          {selectedTeam && (
            <button
              className={`lineup-pill-publish-btn${attendanceMode ? " published" : ""}`}
              onClick={() => setAttendanceMode(v => !v)}
            >
              {attendanceMode ? "Attending ✓" : "Take Attendance"}
            </button>
          )}
        </div>
      </div>

      {publishedNote && (
        <div
          className="card"
          style={{
            padding: "10px 14px",
            borderLeft: "4px solid var(--accent, #122f53)",
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
          }}
        >
          <p className="help" style={{ margin: 0, whiteSpace: "pre-wrap", color: "var(--text)" }}>
            <strong>Coach's note:</strong> {publishedNote}
          </p>
        </div>
      )}

      {/* Status / empty */}
      {teamsLoading && <p className="help" style={{ marginTop: 8 }}>Loading…</p>}
      {!teamsLoading && !selectedTeam && (
        <div className="card" style={{ padding: 12 }}>
          <p className="help">No teams configured.</p>
        </div>
      )}
      {loading && <p className="help" style={{ marginTop: 8 }}>Loading…</p>}
      {err && <p className="help" style={{ color: "red", marginTop: 8 }}>{err}</p>}
      {!loading && !err && selectedTeam && !isPublished && (
        <div className="card" style={{ padding: 12 }}>
          <p className="help">No published lineups for {prettyLong(date)}.</p>
        </div>
      )}
      {!loading && !err && selectedTeam && isPublished && boats.length === 0 && (
        <div className="card" style={{ padding: 12 }}>
          <p className="help">No boats in this lineup.</p>
        </div>
      )}

      {/* Boat columns */}
      {boats.length > 0 && (
        <div className="lineupScroller athlete-lineup">
          {boats.map((b) => (
            <div key={b.id} className="lineupCol">
              <div className="colHeader" title={b.type || ""}>
                <div className="lineup-asset-list">
                  <div className="lineup-asset-item">Hull: {b.hull || ""}</div>
                  <div className="lineup-asset-item">Coach: {b.coach || ""}</div>
                  <div className="lineup-asset-item">Oars: {b.oars || ""}</div>
                  <div className="lineup-asset-item">Cox Box: {b.coxBox || ""}</div>
                  <div className="lineup-asset-item">Speed Coach: {b.speedCoach || ""}</div>
                  <div className="lineup-asset-item">Launch: {b.launch || ""}</div>
                </div>
              </div>
              <div className="seatsCol">
                {(b.seats || []).map((s) => (
                  <div
                    key={s.seat}
                    className={`seat v ${s?.userId || s?.guestName ? "filled" : ""}`}
                    title={seatTitle(s)}
                  >
                    <div className="seatLabel">{s.seat}</div>
                    {s?.userId ? (
                      <div
                        className={`chip block seat-chip ${posClass(s.position)}`}
                        onClick={attendanceMode ? () => toggleCheckin(s.userId) : undefined}
                        style={{
                          cursor: attendanceMode ? "pointer" : "default",
                          display: "flex", alignItems: "center", justifyContent: "space-between",
                          gap: 6, width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box",
                          ...(getPositionColor(s.position, positionColors)
                            ? { backgroundColor: getPositionColor(s.position, positionColors), borderColor: getPositionColor(s.position, positionColors) }
                            : {})
                        }}
                        title={attendanceMode ? "Tap to toggle here/not here" : undefined}
                      >
                        <div className="row seat-chip-row">
                          <div className="seatNum">{s.seat}</div>
                          <div className="seat-details">
                            <div className="seat-name-row">
                              <div className="chipName">{s.name || "Athlete"}</div>
                              {checkins?.[s.userId] === "here" && <span className="checkin-chip here">Here</span>}
                              {checkins?.[s.userId] === "not_here" && <span className="checkin-chip not-here">Not Here</span>}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : s?.guestName ? (
                      <div className="chip block non-roster">
                        <div className="row seat-chip-row">
                          <div className="seatNum">{s.seat}</div>
                          <div className="seat-details">
                            <div className="seat-name-row">
                              <div className="chipName">{s.guestName}</div>
                              <div className="chipMeta">(Guest)</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="empty">—</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- helpers ---------- */
function ymd(d) {
  const dateObj = d instanceof Date ? new Date(d.getTime()) : new Date(`${d}T00:00:00`);
  dateObj.setMinutes(dateObj.getMinutes() - dateObj.getTimezoneOffset());
  return dateObj.toISOString().slice(0, 10);
}
function addDays(d, n) {
  const base = d instanceof Date ? new Date(d.getTime()) : new Date(`${d}T00:00:00`);
  base.setDate(base.getDate() + n);
  return base;
}
function prettyLong(iso) {
  const dt = new Date(iso + "T00:00:00");
  return dt.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}
function seatTitle(s) {
  if (!s?.userId) return s?.guestName || s?.seat || "";
  const parts = [s.name].filter(Boolean);
  if (s.position) parts.push(s.position);
  return parts.join(" • ");
}
function posClass(p) {
  if (!p) return "pos-default";
  const key = String(p).toLowerCase();
  if (key.includes("port")) return "pos-port";
  if (key.includes("starboard")) return "pos-starboard";
  if (key.includes("cox")) return "pos-coxswain";
  if (key.includes("scull")) return "pos-sculler";
  if (key.includes("both")) return "pos-both";
  return "pos-default";
}
function getPositionColor(position, colorMap) {
  if (!position || !colorMap) return null;
  const key = String(position).toLowerCase();
  if (colorMap[key]) return colorMap[key];
  for (const [name, color] of Object.entries(colorMap)) {
    if (key.includes(name) || name.includes(key)) return color;
  }
  return null;
}
function isPracticeType(p) {
  const raw = String(p?.type || "").trim().toLowerCase();
  if (!raw) return true;
  if (raw.includes("practice")) return true;
  if (raw.includes("regatta")) return false;
  if (raw.includes("event")) return false;
  return true;
}
